import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { DragProvider, ReorderableList } from "@activecollab/yank-and-drop";
import classnames from "classnames";
import classNames from "classnames";
import update from "immutability-helper";
import PropTypes from "prop-types";
import { DraggableSubtaskItem } from "~/components/Subtasks/DraggableSubtaskItem";
import RecurringSubtaskItem from "~/components/tasks/CreateRecurringTaskModal/RecurringSubtaskItem";
import { hasLoggedUserElevatedPermissions } from "~/store/selectors/selector.users.jsx";

const RecurringSubtaskList = ({
  projectId,
  subtasks,
  onChangeAssigneeOnSubtask,
  onDeleteSubtask,
  onUpdateSubtask,
  onSubtasksReorder,
  hideFromClients,
  resolvedAssignee,
}) => {
  const lastReorderRef = useRef(null);
  const [editSubtaskIndex, setEditSubtaskIndex] = useState();
  const [items, setItems] = useState(subtasks);
  const orederedRef = useRef(items);

  const editable = useSelector(hasLoggedUserElevatedPermissions);

  useUpdateEffect(() => {
    let data;
    if (hideFromClients) {
      data = resolvedAssignee(hideFromClients, subtasks);
      setItems(data);
      orederedRef.current = data;
    } else {
      setItems(subtasks);
      orederedRef.current = subtasks;
    }
  }, [subtasks, hideFromClients]);

  const handleMove = useCallback((dragIndex, hoverIndex) => {
    setItems((prevState) => {
      lastReorderRef.current = {
        source: prevState[dragIndex],
        target: prevState[hoverIndex],
        before: dragIndex > hoverIndex,
      };
      let data = update(prevState, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevState[dragIndex]],
        ],
      });
      orederedRef.current = data;
      return data;
    });
  }, []);

  const handleSave = useCallback(() => {
    onSubtasksReorder(orederedRef.current);
  }, [onSubtasksReorder]);

  const handleStartEdit = useCallback((index) => {
    return setEditSubtaskIndex(index);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditSubtaskIndex(null);
  }, []);

  return items.length > 0 ? (
    <div className="object_subtasks_open">
      <div className="task_list subtask_list">
        <div
          className={classnames(`tasks sortable_enabled`, {
            editable,
          })}
        >
          <DragProvider>
            <ReorderableList
              onReorder={handleSave}
              dragAxis="y"
              onMove={handleMove}
              className={classNames(
                "SortableList tw-mb-1",
                { "tw--ml-[30px]": items.length > 1 },
                { "tw--ml-[6px]": items.length <= 1 }
              )}
              handle=".drag-handle"
            >
              {items.map((subtask, index) => (
                <DraggableSubtaskItem
                  key={`${subtask?.id}`}
                  disabled={items.length === 1}
                >
                  <RecurringSubtaskItem
                    isInEditMode={index === editSubtaskIndex}
                    index={index}
                    projectId={projectId}
                    onDelete={onDeleteSubtask}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                    onChange={onUpdateSubtask}
                    assigneeId={subtask.assignee_id}
                    name={subtask?.body}
                    onChangeAssignee={onChangeAssigneeOnSubtask}
                    hideFromClients={hideFromClients}
                  />
                </DraggableSubtaskItem>
              ))}
            </ReorderableList>
          </DragProvider>
        </div>
      </div>
    </div>
  ) : null;
};

RecurringSubtaskList.propTypes = {
  users: PropTypes.array,
  projectId: PropTypes.number,
  subtasks: PropTypes.array,
  onChangeAssigneeOnSubtask: PropTypes.func,
  onDeleteSubtask: PropTypes.func,
  onUpdateSubtask: PropTypes.func,
  onSubtasksReorder: PropTypes.func,
  hideFromClients: PropTypes.bool,
  resolvedAssignee: PropTypes.func,
};

export default RecurringSubtaskList;
